/**
 * Per-app session cookie names.
 *
 * The BE sets three auth cookies on login: access_token, refresh_token,
 * csrf_token. By default all three are scoped to the parent domain (e.g.
 * `.staging.homeplated.com`) so that the shared API at api.<env> can
 * cookie-auth from any consumer subdomain. That parent-domain scope means
 * every cookie is visible to every subdomain — fine when there's only one
 * session per browser, broken when a user needs to be logged in as TWO
 * different profiles concurrently (e.g. DevOps Admin holding both a
 * consumer session on app.<env> and a company session on admin.<env>).
 *
 * To support that, admin overrides the three cookie names via env vars
 * (`csrf_admin_token`, `access_admin_token`, `refresh_admin_token`) so its
 * cookies coexist with the consumer triplet in the browser instead of
 * overwriting them. The BE picks which triplet to write at login time
 * from the request's Origin (see api-core-go `sessionCookieNamesForOrigin`);
 * picks which triplet to read on subsequent requests from a content match
 * against the X-CSRF-Token header (see `sessionFamilyForRequest`).
 *
 * Apps that don't override these env vars get the consumer defaults — that
 * covers core + chef (consumer family, intentionally shared).
 *
 * `VITE_SESSION_MODE` is also re-exported here as the single source of
 * truth for "is auth even enabled in this app?". `isAuthEnabled === false`
 * means the auth bootstrap is a no-op (used by www, the marketing site,
 * which has no user identity surface at all).
 */

export const CSRF_COOKIE_NAME = import.meta.env.VITE_CSRF_COOKIE_NAME || 'csrf_token';

export const ACCESS_COOKIE_NAME = import.meta.env.VITE_ACCESS_COOKIE_NAME || 'access_token';

export const REFRESH_COOKIE_NAME = import.meta.env.VITE_REFRESH_COOKIE_NAME || 'refresh_token';

export const SESSION_MODE = import.meta.env.VITE_SESSION_MODE || '';

/**
 * Auth bootstrap noop sentinel. When empty SESSION_MODE is set (currently
 * only www), the synchronous auth-cookie sniff returns false and the
 * async restoreSession returns immediately — no whoami, no PUT /device,
 * no nothing.
 */
export const isAuthEnabled = SESSION_MODE !== '';
